import {
  DevicesAvailabilityState,
  DevicesDeviceSubtype,
  DevicesDeviceType,
  getGetDeviceQueryKey,
  getListDevicesQueryKey,
  useUpdateDevice,
  useUpdateDeviceFavorite,
  useUpdateDeviceState,
  type DevicesDevice,
  type DevicesDeviceState,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, ChevronDown, Copy, Pencil, Power, Star, Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';

import { applyStatePatch, getDeviceIcon, getDeviceOn, getDeviceTypeLabel } from '../utils';
import { getDeviceIdForApi } from '../utils/deviceState';

import {
  FanControls,
  LightControls,
  WindowCoveringControls,
  SpeakerControls,
  SwitchControls,
  TVControls,
} from './controls';

import { IconButton, Select, SlideOutPanel } from '@/components/ui';

export interface DeviceControlModalProps {
  device: DevicesDevice;
  isOpen: boolean;
  onClose: () => void;
  /** Called when user wants to edit a virtual device */
  onEditVirtualDevice?: () => void;
  /** Called when user wants to delete a virtual device */
  onDeleteVirtualDevice?: () => void;
}

function getAvailabilityBadge(availability?: DevicesAvailabilityState) {
  switch (availability) {
    case DevicesAvailabilityState.AvailabilityAvailable:
      return {
        label: 'Available',
        className:
          'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20',
        dotClassName: 'bg-emerald-500',
      };
    case DevicesAvailabilityState.AvailabilityUnavailable:
      return {
        label: 'Unavailable',
        className:
          'bg-red-50 text-red-700 ring-1 ring-red-200 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/20',
        dotClassName: 'bg-red-500',
      };
    case DevicesAvailabilityState.AvailabilityUnknown:
    default:
      return {
        label: 'Unknown',
        className:
          'bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20',
        dotClassName: 'bg-amber-500',
      };
  }
}

/** Get panel size based on device type - TVs and speakers need more space */
function getPanelSize(deviceType?: DevicesDeviceType): 'md' | 'lg' | 'xl' {
  if (
    deviceType === DevicesDeviceType.DeviceTypeTV ||
    deviceType === DevicesDeviceType.DeviceTypeSpeaker
  ) {
    return 'xl';
  }
  return 'lg';
}

export function DeviceControlModal({
  device,
  isOpen,
  onClose,
  onEditVirtualDevice,
  onDeleteVirtualDevice,
}: DeviceControlModalProps) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const queryClient = useQueryClient();

  const Icon = getDeviceIcon(device.type, device.subtype);
  const availabilityInfo = getAvailabilityBadge(device.availability);
  const isOn = getDeviceOn(device);
  const isAvailable = device.availability === DevicesAvailabilityState.AvailabilityAvailable;
  const deviceId = getDeviceIdForApi(device);
  const panelSize = getPanelSize(device.type);
  const isVirtualDevice = device.provider === 'virtual';

  const { mutate: updateDeviceState } = useUpdateDeviceState({
    mutation: {
      onMutate: async ({ id, data: stateData }) => {
        setError(null);
        await queryClient.cancelQueries({ queryKey: getListDevicesQueryKey() });

        const previousData = queryClient.getQueryData<{ devices: DevicesDevice[] }>(
          getListDevicesQueryKey()
        );

        // Optimistic update for list view
        queryClient.setQueryData<{ devices: DevicesDevice[] }>(getListDevicesQueryKey(), (old) => {
          if (!old?.devices) return old;
          return {
            ...old,
            devices: old.devices.map((d) => {
              if (d.id === id || d.humanId === id) {
                return {
                  ...d,
                  state: applyStatePatch(d.state, stateData.state),
                };
              }
              return d;
            }),
          };
        });

        // Also update device detail cache if present
        const detailKey = getGetDeviceQueryKey(id);
        const previousDetail = queryClient.getQueryData<{ device: DevicesDevice }>(detailKey);
        if (previousDetail) {
          queryClient.setQueryData<{ device: DevicesDevice }>(detailKey, (old) => {
            if (!old?.device) return old;
            return {
              ...old,
              device: {
                ...old.device,
                state: applyStatePatch(old.device.state, stateData.state),
              },
            };
          });
        }

        return { previousData, previousDetail };
      },
      onError: (err, { id }, context) => {
        // Rollback on error
        if (context?.previousData) {
          queryClient.setQueryData(getListDevicesQueryKey(), context.previousData);
        }
        if (context?.previousDetail) {
          queryClient.setQueryData(getGetDeviceQueryKey(id), context.previousDetail);
        }
        setError('Failed to update device. Please try again.');
      },
      onSettled: (_data, _err, { id }) => {
        // Invalidate to ensure fresh data
        queryClient.invalidateQueries({ queryKey: getGetDeviceQueryKey(id) });
        queryClient.invalidateQueries({ queryKey: getListDevicesQueryKey() });
      },
    },
  });

  const { mutate: updateDevice, isPending: isUpdatingDevice } = useUpdateDevice({
    mutation: {
      onMutate: async ({ id, data }) => {
        setError(null);
        await queryClient.cancelQueries({ queryKey: getListDevicesQueryKey() });

        const previousData = queryClient.getQueryData<{ devices: DevicesDevice[] }>(
          getListDevicesQueryKey()
        );

        queryClient.setQueryData<{ devices: DevicesDevice[] }>(getListDevicesQueryKey(), (old) => {
          if (!old?.devices) return old;
          return {
            ...old,
            devices: old.devices.map((d) => {
              if (d.id === id || d.humanId === id) {
                return {
                  ...d,
                  subtype: data.subtype,
                };
              }
              return d;
            }),
          };
        });

        const detailKey = getGetDeviceQueryKey(id);
        const previousDetail = queryClient.getQueryData<{ device: DevicesDevice }>(detailKey);
        if (previousDetail) {
          queryClient.setQueryData<{ device: DevicesDevice }>(detailKey, (old) => {
            if (!old?.device) return old;
            return {
              ...old,
              device: {
                ...old.device,
                subtype: data.subtype,
              },
            };
          });
        }

        return { previousData, previousDetail };
      },
      onError: (_err, { id }, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(getListDevicesQueryKey(), context.previousData);
        }
        if (context?.previousDetail) {
          queryClient.setQueryData(getGetDeviceQueryKey(id), context.previousDetail);
        }
        setError('Failed to update device settings. Please try again.');
      },
      onSettled: (_data, _err, { id }) => {
        queryClient.invalidateQueries({ queryKey: getGetDeviceQueryKey(id) });
        queryClient.invalidateQueries({ queryKey: getListDevicesQueryKey() });
      },
    },
  });

  const { mutate: updateDeviceFavorite, isPending: isUpdatingFavorite } = useUpdateDeviceFavorite({
    mutation: {
      onMutate: async ({ id, data }) => {
        setError(null);
        await queryClient.cancelQueries({ queryKey: getListDevicesQueryKey() });

        const previousData = queryClient.getQueryData<{ devices: DevicesDevice[] }>(
          getListDevicesQueryKey()
        );

        queryClient.setQueryData<{ devices: DevicesDevice[] }>(getListDevicesQueryKey(), (old) => {
          if (!old?.devices) return old;
          return {
            ...old,
            devices: old.devices.map((d) => {
              if (d.id === id || d.humanId === id) {
                return {
                  ...d,
                  isFavorite: data.isFavorite,
                };
              }
              return d;
            }),
          };
        });

        const detailKey = getGetDeviceQueryKey(id);
        const previousDetail = queryClient.getQueryData<{ device: DevicesDevice }>(detailKey);
        if (previousDetail) {
          queryClient.setQueryData<{ device: DevicesDevice }>(detailKey, (old) => {
            if (!old?.device) return old;
            return {
              ...old,
              device: {
                ...old.device,
                isFavorite: data.isFavorite,
              },
            };
          });
        }

        return { previousData, previousDetail };
      },
      onError: (_err, { id }, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(getListDevicesQueryKey(), context.previousData);
        }
        if (context?.previousDetail) {
          queryClient.setQueryData(getGetDeviceQueryKey(id), context.previousDetail);
        }
        setError('Failed to update favorites. Please try again.');
      },
      onSettled: (_data, _err, { id }) => {
        queryClient.invalidateQueries({ queryKey: getGetDeviceQueryKey(id) });
        queryClient.invalidateQueries({ queryKey: getListDevicesQueryKey() });
      },
    },
  });

  const handleUpdateState = useCallback(
    (state: DevicesDeviceState) => {
      if (!deviceId) return;
      updateDeviceState({ id: deviceId, data: { state } });
    },
    [deviceId, updateDeviceState]
  );

  const windowCoveringSubtype = device.subtype ?? DevicesDeviceSubtype.DeviceSubtypeRollerShade;
  const windowCoveringSubtypeOptions = [
    { value: DevicesDeviceSubtype.DeviceSubtypeRollerShade, label: 'Roller shade' },
    { value: DevicesDeviceSubtype.DeviceSubtypeCurtain, label: 'Curtain' },
  ];

  const handleSubtypeChange = useCallback(
    (subtype: string) => {
      if (!deviceId || subtype === windowCoveringSubtype) return;
      updateDevice({ id: deviceId, data: { subtype: subtype as DevicesDeviceSubtype } });
    },
    [deviceId, updateDevice, windowCoveringSubtype]
  );

  const handleCopyHumanId = async () => {
    if (device.humanId) {
      await navigator.clipboard.writeText(device.humanId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const subtitle = [getDeviceTypeLabel(device.type), device.provider].filter(Boolean).join(' · ');
  const favoriteLabel = device.isFavorite ? 'Remove from favorites' : 'Add to favorites';

  const handleToggleFavorite = useCallback(() => {
    if (!deviceId) return;
    updateDeviceFavorite({ id: deviceId, data: { isFavorite: !device.isFavorite } });
  }, [deviceId, device.isFavorite, updateDeviceFavorite]);

  // Render device-specific controls based on type
  const renderDeviceControls = () => {
    const controlsDisabled = !isAvailable;

    switch (device.type) {
      case DevicesDeviceType.DeviceTypeSwitch:
        return (
          <SwitchControls
            device={device}
            onUpdateState={handleUpdateState}
            disabled={controlsDisabled}
          />
        );
      case DevicesDeviceType.DeviceTypeLight:
        return (
          <LightControls
            device={device}
            onUpdateState={handleUpdateState}
            disabled={controlsDisabled}
          />
        );
      case DevicesDeviceType.DeviceTypeWindowCovering:
        return (
          <WindowCoveringControls
            device={device}
            onUpdateState={handleUpdateState}
            disabled={controlsDisabled}
          />
        );
      case DevicesDeviceType.DeviceTypeFan:
        return (
          <FanControls
            device={device}
            onUpdateState={handleUpdateState}
            disabled={controlsDisabled}
          />
        );
      case DevicesDeviceType.DeviceTypeTV:
        return (
          <TVControls
            device={device}
            onUpdateState={handleUpdateState}
            disabled={controlsDisabled}
          />
        );
      case DevicesDeviceType.DeviceTypeSpeaker:
        return (
          <SpeakerControls
            device={device}
            onUpdateState={handleUpdateState}
            disabled={controlsDisabled}
          />
        );
      default:
        return (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 p-4 text-center dark:border-white/[0.06] dark:bg-white/[0.02]">
            <p className="text-sm text-gray-500 dark:text-zinc-500">
              Advanced controls for this device type coming soon
            </p>
          </div>
        );
    }
  };

  const virtualDeviceFooter =
    isVirtualDevice && (onEditVirtualDevice || onDeleteVirtualDevice) ? (
      <>
        {onEditVirtualDevice && (
          <button
            type="button"
            onClick={onEditVirtualDevice}
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
          >
            <Pencil className="h-4 w-4" />
            Edit Configuration
          </button>
        )}
        {onDeleteVirtualDevice && (
          <button
            type="button"
            onClick={onDeleteVirtualDevice}
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        )}
      </>
    ) : undefined;

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={onClose}
      title={device.name || device.humanId || 'Device'}
      subtitle={subtitle}
      size={panelSize}
      footer={virtualDeviceFooter}
    >
      <div className="space-y-4">
        {/* Device Header Card - stateful appearance */}
        <div
          className={`rounded-xl p-4 ${
            isOn
              ? 'bg-amber-50 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:ring-amber-500/20'
              : 'bg-gray-50 ring-1 ring-gray-200 dark:bg-zinc-800/50 dark:ring-white/[0.06]'
          }`}
        >
          <div className="flex items-center justify-between gap-4">
            {/* Stateful icon */}
            <div
              className={`flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl ${
                isOn
                  ? 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400'
                  : 'bg-gray-100 text-gray-400 dark:bg-zinc-700 dark:text-zinc-500'
              }`}
            >
              <Icon className="h-7 w-7" />
            </div>

            {/* Status badges */}
            <div className="flex flex-1 flex-wrap gap-2">
              {/* Availability badge */}
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${availabilityInfo.className}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${availabilityInfo.dotClassName}`} />
                {availabilityInfo.label}
              </span>

              {/* Power state chip */}
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${
                  isOn
                    ? 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20'
                    : 'bg-gray-50 text-gray-600 ring-gray-200 dark:bg-zinc-800 dark:text-zinc-400 dark:ring-white/[0.06]'
                }`}
              >
                <Power className="h-3 w-3" />
                {isOn === null ? 'Unknown' : isOn ? 'On' : 'Off'}
              </span>
            </div>

            <IconButton
              icon={<Star fill={device.isFavorite ? 'currentColor' : 'none'} />}
              label={favoriteLabel}
              variant="ghost"
              size="sm"
              loading={isUpdatingFavorite}
              onClick={handleToggleFavorite}
              className={
                device.isFavorite
                  ? 'text-amber-500 hover:text-amber-600 dark:text-amber-400'
                  : 'text-gray-500 hover:text-amber-500 dark:text-zinc-400 dark:hover:text-amber-400'
              }
            />
          </div>
        </div>

        {/* Unavailable device message */}
        {!isAvailable && (
          <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>Device unavailable. Controls are disabled.</span>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Device-specific controls in structured section */}
        <div className="rounded-xl bg-gray-50 p-4 dark:bg-zinc-800/50">
          {renderDeviceControls()}
        </div>

        {/* Details disclosure */}
        {(device.humanId || device.type === DevicesDeviceType.DeviceTypeWindowCovering) && (
          <details
            className="group"
            open={detailsOpen}
            onToggle={(e) => setDetailsOpen((e.target as HTMLDetailsElement).open)}
          >
            <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300">
              <ChevronDown
                className={`h-4 w-4 transition-transform ${detailsOpen ? 'rotate-180' : ''}`}
              />
              Details
            </summary>
            <div className="mt-3 rounded-lg bg-gray-50 p-3 dark:bg-zinc-800/50">
              {(device.humanId || device.id) && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 dark:text-zinc-500">Device ID</span>
                  <div className="flex items-center gap-2">
                    <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600 dark:bg-zinc-800 dark:text-zinc-400">
                      {device.humanId || device.id}
                    </code>
                    {device.humanId && (
                      <button
                        type="button"
                        onClick={handleCopyHumanId}
                        className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-zinc-500 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
                        aria-label="Copy device ID"
                      >
                        {copied ? (
                          <Check className="h-3.5 w-3.5 text-emerald-500" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}
              {device.type === DevicesDeviceType.DeviceTypeWindowCovering && (
                <div className="mt-3">
                  <span className="text-xs text-gray-500 dark:text-zinc-500">Subtype</span>
                  <Select
                    label="Subtype"
                    hideLabel
                    value={windowCoveringSubtype}
                    onChange={handleSubtypeChange}
                    options={windowCoveringSubtypeOptions}
                    disabled={isUpdatingDevice}
                    className="mt-2"
                    fullWidth
                  />
                </div>
              )}
            </div>
          </details>
        )}
      </div>
    </SlideOutPanel>
  );
}
